import { Check, RefreshCw } from "lucide-react";
import { useMemo, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import { useRequestsData } from "../data/RequestsDataContext";
import { countSteps, TEMPLATE_GROUPS } from "../model";
import type { NewRequestAction } from "../types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (action: NewRequestAction) => void;
};

type Pick = { saved: string } | { catalog: string[] } | null;

const CheckBox: FC<{ on: boolean; round?: boolean }> = ({ on, round }) => (
  <span
    className={cn(
      "flex size-4 shrink-0 items-center justify-center ring-1 ring-line-3",
      round ? "rounded-full" : "rounded-sm",
      on && "bg-primary text-primary-foreground ring-primary"
    )}
  >
    {on ? <Check className="size-3" /> : null}
  </span>
);

/**
 * "From a Template…": one of the firm's templates (its note, video, expiry
 * and reminders come along), or one or more CaseBlink forms combined in the
 * order picked. The draft opens to be edited before sending.
 */
export const NewRequestDialog: FC<Props> = ({
  open,
  onOpenChange,
  onCreate
}) => {
  const data = useRequestsData();
  const [pick, setPick] = useState<Pick>(null);

  const firm = useMemo(
    () => data.savedTemplates.filter((t) => !t.archived_at),
    [data.savedTemplates]
  );
  const groups = useMemo(
    () =>
      TEMPLATE_GROUPS.filter((g) => g.id !== "saved").map((g) => ({
        ...g,
        items: data.templates.filter(
          (t) =>
            (t.category ?? "building_block") === g.id &&
            t.template_id !== "custom"
        )
      })),
    [data.templates]
  );

  const catalog = pick && "catalog" in pick ? pick.catalog : [];
  const savedId = pick && "saved" in pick ? pick.saved : null;
  const toggleCatalog = (id: string) => {
    const next = catalog.includes(id)
      ? catalog.filter((x) => x !== id)
      : [...catalog, id];
    setPick(next.length ? { catalog: next } : null);
  };

  const close = (o: boolean) => {
    if (!o) setPick(null);
    onOpenChange(o);
  };

  const create = () => {
    if (savedId) onCreate({ kind: "saved", templateId: savedId });
    else if (catalog.length)
      onCreate({ kind: "templates", templateIds: catalog });
    close(false);
  };

  let summary = "Nothing selected";
  if (savedId)
    summary = firm.find((t) => t.template_id === savedId)?.name ?? "1 selected";
  else if (catalog.length) summary = `${catalog.length} selected`;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="px-4 pt-4 pb-3">
          <DialogTitle>New Request From a Template</DialogTitle>
          <DialogDescription>
            Start from one of your firm&apos;s templates, or combine USCIS forms
            in the order you pick them.
          </DialogDescription>
        </DialogHeader>
        <Command className="rounded-none border-y border-line-1">
          <CommandInput placeholder="Search templates…" />
          <CommandList className="max-h-80">
            {data.isTemplatesLoading && !data.templates.length ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-fg-3">
                <Spinner /> Loading templates…
              </div>
            ) : data.templatesError && !data.templates.length ? (
              <div className="flex flex-col items-center gap-2 py-6 text-sm text-danger">
                {data.templatesError}
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={data.retryTemplates}
                >
                  <RefreshCw /> Retry
                </Button>
              </div>
            ) : (
              <CommandEmpty>No template matches.</CommandEmpty>
            )}
            {firm.length ? (
              <CommandGroup heading="Firm Templates">
                {firm.map((t) => {
                  const n = countSteps(t.schema ?? []);
                  return (
                    <CommandItem
                      key={t.template_id}
                      value={`firm ${t.name} ${t.visa ?? ""}`}
                      onSelect={() =>
                        setPick(
                          savedId === t.template_id
                            ? null
                            : { saved: t.template_id }
                        )
                      }
                      data-testid={`firm-template-${t.template_id}`}
                    >
                      <CheckBox on={savedId === t.template_id} round />
                      <span className="flex-1 truncate">{t.name}</span>
                      <span className="text-sm text-fg-3">
                        {n} {n === 1 ? "question" : "questions"}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
            {groups.map((g) =>
              g.items.length ? (
                <CommandGroup key={g.id} heading={g.label}>
                  {g.items.map((t) => (
                    <CommandItem
                      key={t.template_id}
                      value={`${t.label} ${t.visa ?? ""} ${t.agency ?? ""}`}
                      onSelect={() => toggleCatalog(t.template_id)}
                    >
                      <CheckBox on={catalog.includes(t.template_id)} />
                      <span className="flex-1 truncate">{t.label}</span>
                      <span className="text-sm text-fg-3">
                        {t.question_count ?? 0} · {t.document_count ?? 0}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null
            )}
          </CommandList>
        </Command>
        <DialogFooter className="mx-0 mb-0 items-center rounded-none border-t-0 px-4 py-3">
          <span className="mr-auto min-w-0 truncate text-sm text-fg-3">
            {summary}
          </span>
          <Button variant="ghost" size="sm" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" disabled={!pick} onClick={create}>
            Create Draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
